import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Sidebar.css';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/reducers/authSlice';
import logo from '../Assets/Images/siteIcon.png';

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────

// Confirm the "/api" segment matches your other working calls
// (e.g. login) built off REACT_APP_WEBSITE_URL.
const MENU_API_URL = `${process.env.REACT_APP_WEBSITE_URL}/api/customer-active-menus`;

// ─────────────────────────────────────────────────────────────
// ICON MAP — keyed by the `icon` string stored in menu/sub_menu.
// Unknown keys fall back to DEFAULT_ICON, so nothing renders blank.
// ─────────────────────────────────────────────────────────────

const ICONS = {
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"></rect>
      <rect x="14" y="3" width="7" height="7"></rect>
      <rect x="14" y="14" width="7" height="7"></rect>
      <rect x="3" y="14" width="7" height="7"></rect>
    </svg>
  ),
  profile: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 17c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  'punch-in': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 10h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  'punch-out': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 10h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  jobs: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2.5" y="6" width="15" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 6V4a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2.5 10h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  attendance: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2.5" y="4" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 2v4M13 2v4M2.5 9h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="7" cy="13" r="1" fill="currentColor" />
      <circle cx="10" cy="13" r="1" fill="currentColor" />
      <circle cx="13" cy="13" r="1" fill="currentColor" />
    </svg>
  ),
  accounts: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2.5" y="4" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 2v4M13 2v4M2.5 9h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="7" cy="13" r="1" fill="currentColor" />
      <circle cx="10" cy="13" r="1" fill="currentColor" />
      <circle cx="13" cy="13" r="1" fill="currentColor" />
    </svg>
  ),
  reports: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2.5" y="4" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 2v4M13 2v4M2.5 9h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="7" cy="13" r="1" fill="currentColor" />
      <circle cx="10" cy="13" r="1" fill="currentColor" />
      <circle cx="13" cy="13" r="1" fill="currentColor" />
    </svg>
  ),
  leadger: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 2.5h7l3 3v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 2.5v3h3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M6.5 10h7M6.5 12.5h7M6.5 15h4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  supervisor: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="6.5" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 17c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M14.5 8.5l1 1 2-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  'approve-reg': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="2.5" y="4" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 2v4M13 2v4M2.5 9h15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M6.5 13l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  'approve-leave': (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2l2.09 4.26L17 7.27l-3.5 3.41.83 4.82L10 13.27l-4.33 2.23.83-4.82L3 7.27l4.91-.71L10 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  leave: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2l2.09 4.26L17 7.27l-3.5 3.41.83 4.82L10 13.27l-4.33 2.23.83-4.82L3 7.27l4.91-.71L10 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  query: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M17 3H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3l4 3 4-3h3a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M10 7v.5M10 10.5v2.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};

const DEFAULT_ICON = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M10 6.5v4M10 13v.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const getIcon = (key, size = 20) => {
  const svg = ICONS[(key || '').toLowerCase()] || DEFAULT_ICON;
  return React.cloneElement(svg, { width: size, height: size });
};

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const { user } = useSelector((state) => state.auth);
  const { permissions = [] } = useSelector((state) => state.globalVariables);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openLabel, setOpenLabel] = useState(null);

  const hasPermission = useCallback(
    (permission) => permission === null || permission === undefined || permissions.includes(permission),
    [permissions]
  );

  // ─────────────────────────────────────────
  // FETCH MENUS
  // ─────────────────────────────────────────

  useEffect(() => {
    let isMounted = true;

    const fetchMenus = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token'); // adjust to your auth storage

        const res = await axios.post(
          MENU_API_URL,
          {},
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        if (isMounted && res?.data?.status === 200) {
          setMenuData(res.data.data || []);
        }
      } catch (err) {
        if (isMounted) setError(err);
        console.error('Failed to load sidebar menus:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMenus();
    return () => {
      isMounted = false;
    };
  }, []);

  // ─────────────────────────────────────────
  // BUILD NAV TREE FROM API RESPONSE
  // Routes come straight from the DB — no prefix rewriting.
  // Each menu becomes either a simple link (no submenus)
  // or a dropdown group (has submenus), permission-filtered.
  // ─────────────────────────────────────────

  const navItems = useMemo(() => {
    return [...menuData]
      .filter((m) => m.is_active && hasPermission(m.permission))
      .sort((a, b) => (a.order_id ?? 999) - (b.order_id ?? 999))
      .map((m) => {
        const submenus = (m.submenus || [])
          .filter((sm) => sm.is_active && hasPermission(sm.permission))
          .sort((a, b) => (a.sub_order_id ?? 999) - (b.sub_order_id ?? 999))
          .map((sm) => ({
            key: `sub-${sm.id}`,
            label: sm.name,
            to: sm.routes,
            icon: getIcon(sm.icon, 16),
          }))
          .filter((item) => !!item.to);

        return {
          key: `menu-${m.id}`,
          label: m.name,
          to: m.routes,
          icon: getIcon(m.icon),
          children: submenus.length > 0 ? submenus : null,
        };
      })
      // Drop groups that end up with no visible children and no direct route
      .filter((item) => item.to || (item.children && item.children.length > 0));
  }, [menuData, hasPermission]);

  // Keep a submenu open if the current route matches one of its children
  useEffect(() => {
    const match = navItems.find(
      (item) => item.children && item.children.some((c) => location.pathname.startsWith(c.to))
    );
    if (match) setOpenLabel(match.label);
  }, [location.pathname, navItems]);

  const toggleSubmenu = (label) => {
    setOpenLabel((prev) => (prev === label ? null : label));
  };

  // ─────────────────────────────────────────
  // LOGOUT / USER INITIALS
  // ─────────────────────────────────────────

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const initials = user?.name?.split(' ').map((n) => n[0]).join('') || 'U';

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  const renderLink = (item) => (
    <NavLink
      key={item.key}
      to={item.to}
      className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
      onClick={onMobileClose}
      title={collapsed ? item.label : undefined}
    >
      <span className="sidebar-item-icon">{item.icon}</span>
      {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
      {collapsed && <span className="sidebar-tooltip">{item.label}</span>}
    </NavLink>
  );

  const renderGroup = (item) => {
    const isOpen = openLabel === item.label;
    const isChildActive = item.children.some((c) => location.pathname.startsWith(c.to));

    return (
      <div key={item.key} className="sidebar-item-group">
        <button
          type="button"
          className={`sidebar-item sidebar-item-parent ${isChildActive ? 'active' : ''}`}
          onClick={() => toggleSubmenu(item.label)}
          title={collapsed ? item.label : undefined}
        >
          <span className="sidebar-item-icon">{item.icon}</span>
          {!collapsed && <span className="sidebar-item-label">{item.label}</span>}
          {!collapsed && (
            <svg className={`sidebar-chevron ${isOpen ? 'sidebar-chevron-open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M4 5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {collapsed && <span className="sidebar-tooltip">{item.label}</span>}
        </button>

        {(isOpen || collapsed) && (
          <div className={`sidebar-submenu ${collapsed ? 'sidebar-submenu-collapsed' : ''}`}>
            {item.children.map((child) => (
              <NavLink
                key={child.key}
                to={child.to}
                className={({ isActive }) => `sidebar-subitem ${isActive ? 'active' : ''}`}
                onClick={onMobileClose}
                title={collapsed ? child.label : undefined}
              >
                <span className="sidebar-subitem-icon">{child.icon}</span>
                {!collapsed && <span className="sidebar-subitem-label">{child.label}</span>}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {mobileOpen && <div className="sidebar-overlay" onClick={onMobileClose} />}

      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {!collapsed && (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px 0' }}>
                <img
                  src={logo}
                  alt="WorkSphere"
                  style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff' }}
                />
              </div>
            )}
          </div>

          <button className="sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d={collapsed ? 'M6 4l6 5-6 5' : 'M12 4l-6 5 6 5'}
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          {loading && !collapsed && <div className="sidebar-loading">Loading menu…</div>}
          {error && !collapsed && <div className="sidebar-error">Couldn't load menu</div>}

          {navItems.map((item) => (item.children ? renderGroup(item) : renderLink(item)))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            {!collapsed && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{user?.name}</span>
                <span className="sidebar-user-role">{user?.designation}</span>
              </div>
            )}
          </div>
          <button className="sidebar-logout" onClick={handleLogout} title="Logout">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M7 15H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M12 12l4-3-4-3M16 9H7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </aside>
    </>
  );
}